
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TreeMapFilterable from '@/components/reports-v3/components/filterables/TreeMapFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {
	PermissionsGroup,
} from '@/store/models.def';
import { getTableName } from '@/worker/db/sqlHelpers';
import * as echarts from 'echarts';
import SwitchAmountQuantityToggle from '@/components/SwitchAmountQuantityToggle.vue';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import settingx from '@/store/modules/settingx';
import { TooltipFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tooltipFormatter';
import { DataViewFormatter } from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import gdbx from '@/store/modules/gdbx';

@Component({
	components: {
		TreeMapFilterable,
		SwitchAmountQuantityToggle,
		SelectFilter,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
	},
})
export default class StockSalesTreeMap extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['stocks', 'sales'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['stockItems', 'agents', 'dateRange'];

	public result: any = [];
	public switchAmountQuantity: string = 'amount';
	public series: echarts.ECharts[] | any = [];
	public treeMapName: string = 'Stock Group';
	public manualLoadingProgess = 0;

	public selectedFilter: string = this.filterOptions[0];

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

  public get filterOptions() {
    const filterOptionsListAmount = [
      'Top 10 for each category',
      'Bottom 10 for each category',
      'All',
    ];

    const filterOptionsListQuantity = [
      'Top 10 for each category',
      'Bottom 10 for each category',
      'All',
      'F.O.C.',
    ];
    return this.switchAmountQuantity === 'amount' ? filterOptionsListAmount : filterOptionsListQuantity;
  }

	public get dateFormatted() {
		return this.selectedDateRange;
	}

	public get tooltipFormatter() {
		return this.switchAmountQuantity === 'amount'
			? TooltipFormatter.basicTreeMap
			: TooltipFormatter.treeMapWithoutCurrency;
	}

	public get dataViewFormatter() {
		return this.switchAmountQuantity === 'amount'
			? DataViewFormatter.treemap
			: DataViewFormatter.treemapWithoutCurrency;
	}

	public triggerSwitchAmountQuantity(value) {
		this.switchAmountQuantity = value;
	}

	public get expensiveHook() {
		const {
			includeCreditNote,
			includeDebitNote,
			switchAmountQuantity,
			selectedDateRange,
			selectedAgents,
			selectedStockItems,
			selectedFilter,
		} = this;
		return JSON.stringify([
			includeCreditNote,
			includeDebitNote,
			switchAmountQuantity,
			selectedDateRange,
			selectedAgents,
			selectedStockItems,
			selectedFilter,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.3);

		const queryHead = await this._ensureTablesLoaded(
			['stockSales', 'globalStocks'] as any[],
		);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.5);

		const dateFilter = `TRY_CAST("POSTDATE" AS BIGINT) >= ${this.selectedDateRange[0]} AND TRY_CAST("POSTDATE" AS BIGINT) <= ${this.selectedDateRange[1]}`;

		// Build doctype filter
		const allowedDocTypes = ['IV', 'DO', 'CS'];
		if (this.includeDebitNote) {
			allowedDocTypes.push('DN');
		}
		if (this.includeCreditNote) {
			allowedDocTypes.push('CN');
		}
		const docTypeFilter = ` AND "DOCTYPE" IN (${allowedDocTypes.map((d) => `'${d}'`).join(', ')})`;

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		let stockItemFilter = '';
		if (this.selectedStockItems && this.selectedStockItems.length > 0) {
			const list = this.selectedStockItems.map((s: string) => `'${escapeStr(s)}'`).join(', ');
			stockItemFilter = ` AND "ITEMCODE" IN (${list})`;
		}

		// Determine value expression based on amount/quantity toggle
		const isAmount = this.switchAmountQuantity === 'amount';
		const valueExpr = isAmount
			? 'SUM(-(TRY_CAST("PRICE" AS DOUBLE) * TRY_CAST("QTY" AS DOUBLE)))'
			: 'SUM(-TRY_CAST("QTY" AS DOUBLE))';

		// For F.O.C. mode, only include rows where QTY = 0
		const focFilter = (!isAmount && this.selectedFilter === 'F.O.C.')
			? ' AND TRY_CAST("QTY" AS DOUBLE) = 0'
			: '';

		// Query grouped by stockGroup + itemCode
		const salesQuery = queryHead.rawQuery(`
			SELECT "STOCKGROUP", "ITEMCODE",
				${valueExpr} as val
			FROM ${getTableName('stockSales')}
			WHERE ${dateFilter}${docTypeFilter}${stockItemFilter}${focFilter}
			GROUP BY "STOCKGROUP", "ITEMCODE"
		`);

		// Query stock descriptions
		const stockQuery = queryHead.rawQuery(`
			SELECT "CODE" as item_code, "DESCRIPTION" as item_desc,
				"STOCKGROUP", "STOCKGROUPDESC"
			FROM ${getTableName('globalStocks')}
		`);

		const [salesRows, stockRows] = await Promise.all([salesQuery, stockQuery]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.9, 1);

		// Build stock lookup maps
		const stockItemNames: { [key: string]: string } = {};
		const stockGroupDescMap: { [key: string]: string } = {};
		for (const row of stockRows) {
			stockItemNames[row.item_code] = row.item_desc || '';
			if (row.STOCKGROUP && row.STOCKGROUPDESC) {
				stockGroupDescMap[row.STOCKGROUP] = row.STOCKGROUPDESC;
			}
		}

		// Build nested: stockGroup -> items
		const groupMap = new Map<string, Array<{ itemCode: string; val: number }>>();
		for (const row of salesRows) {
			const grp = row.STOCKGROUP || '';
			if (!groupMap.has(grp)) { groupMap.set(grp, []); }
			groupMap.get(grp)!.push({
				itemCode: row.ITEMCODE || '',
				val: Number(row.val) || 0,
			});
		}

		const treeColors = [
			'#8975ea', '#acbeff', '#accdff', '#acdaff', '#cdc8f9', '#c7b5e2',
		];

		this.result = [];

		groupMap.forEach((items, stockGroup) => {
			const groupLabel = stockGroupDescMap[stockGroup]
				? `[${stockGroup}]${stockGroupDescMap[stockGroup]}`
				: stockGroup;

			const children: Array<{ name: string; value: number }> = [];
			let groupValue = 0;

			for (const item of items) {
				const itemName = stockItemNames[item.itemCode]
					? '[' + item.itemCode + '] ' + stockItemNames[item.itemCode]
					: '--';
				if (stockItemNames[item.itemCode] !== undefined) {
					children.push({ name: itemName, value: item.val });
				}
				groupValue += item.val;
			}

			// Apply top/bottom filter
			if (this.selectedFilter === 'Top 10 for each category') {
				children.sort((a, b) => b.value - a.value);
				if (children.length > 10) {
					children.splice(10, children.length - 10);
				}
			} else if (this.selectedFilter === 'Bottom 10 for each category') {
				children.sort((a, b) => a.value - b.value);
				if (children.length > 10) {
					children.splice(10, children.length - 10);
				}
			}

			this.result.push({
				name: groupLabel,
				value: groupValue,
				color: treeColors,
				colorSaturation: [0.9, 0.4],
				children,
			});
		});

		  this.series = [];

		  const stock = {
			name: this.treeMapName,
			type: 'treemap',
			roam: 'move',
			width: '100%',
			// drillDownIcon: '👆🏻',
			drillDownIcon: '',
			// leafDepth: 1,
			label: {
				color: '#000',
			},
			itemStyle: {
				gapWidth: 1,
				borderWidth: 1,
			},
			levels: [
				{
					itemStyle: {
						// borderColor: '#555',
						borderWidth: 2,
						gapWidth: 2,
					},
					upperLabel: {
						show: false,
						fontWeight: 'bold',
						color: '#000',
					},
					emphasis: {
						itemStyle: {
							borderWidth: 0,
							gapWidth: 2,
						},
						upperLabel: {
							show: true,
							fontWeight: 'bold',
							color: '#000',
						},
					},
				},
				{
					colorSaturation: [0.3, 0.6],
					itemStyle: {
						borderColorSaturation: 0.7,
						gapWidth: 2,
						borderWidth: 2,
					},
					upperLabel: {
						show: true,
						color: '#000',
						fontWeight: 'bold',
					},
					emphasis: {
						itemStyle: {
							borderWidth: 0,
							gapWidth: 1,
						},
						upperLabel: {
							show: true,
							color: '#000',
							fontWeight: 'bold',
						},
					},
				},
				{
					itemStyle: {
						// borderColor: '#555',
						gapWidth: 1,
						borderWidth: 1,
					},
					upperLabel: {
						show: true,
						color: '#000',
						fontWeight: 'bold',
					},
					emphasis: {
						itemStyle: {
							borderWidth: 0,
							gapWidth: 1,
						},
						upperLabel: {
							show: true,
							color: '#000',
							fontWeight: 'bold',
						},
					},
				},
			],
			data: this.result,
		};
		  this.series = [stock];

		  this.saveHistory('result', 'series', 'switchAmountQuantity', 'treeMapName');
	}

  public onHovering(value: boolean, filterId: string) {
    this.$emit('hovering', value, filterId);
  }
  public onEditing(value: boolean, filterId: string) {
    this.$emit('editing', value, filterId);
  }
}
